import { useContext, useMemo, useState } from 'react'
import useAsyncEffect from 'utils/src/hooks/use-async-effect'
import { CONTROL_SIZE_SLIM, PREVIEW_WIDTH } from '../../constants'
import { EditPanelApiContext } from '../../contexts/api'
import { EditPanelContext } from '../../contexts/panel'
import Field from '../../field'
import { hasFieldPermission } from '../../utilities/permissions'
import { getPreviewHeight, getViewString } from '../../utilities/preview'
import { Footer, Preview, Views, ViewsIcon } from './style'

type Props = {
    className?: string
}

const EditPanelInfoPreview = ({ className }: Props) => {
    const { fetchViewCount } = useContext(EditPanelApiContext)
    const { colors, gifs, isCompact, permissions, width: panelWidth } = useContext(EditPanelContext)
    const [viewCount, setViewCount] = useState<number | undefined>()
    const previewWidth = isCompact ? panelWidth : PREVIEW_WIDTH

    const longViews = useMemo(() => {
        return `${getViewString(viewCount || 0)} Views`
    }, [viewCount, isCompact])

    const shortViews = useMemo(() => {
        return `${getViewString(viewCount || 0, true)}${isCompact ? '' : ' Views'}`
    }, [viewCount, isCompact])

    const gifIdString = useMemo(() => gifs.map((g) => g.id).join(','), [gifs])

    useAsyncEffect(async () => {
        let cancelled = false
        try {
            if (gifs.length === 0) {
                if (!cancelled) {
                    setViewCount(0)
                }
                return
            }
            const firstGif = gifs[0]
            const firstCount = await fetchViewCount(`${firstGif.id}`)
            if (!cancelled) {
                setViewCount(firstCount || 0)
            }
        } catch (err) {
            if (!cancelled) {
                console.error(`error fetching count`, err)
            }
        }
        return () => {
            cancelled = true
        }
    }, [gifIdString])

    return (
        <div className={className}>
            <Preview
                backgroundColor={colors.controlDark}
                badgeColor={colors.badgeColor}
                gifs={gifs}
                height={getPreviewHeight(gifs[0], previewWidth)}
                width={previewWidth}
            />
            <Footer>
                {isCompact && hasFieldPermission('privacy', permissions) && (
                    <Field field="privacy" size={CONTROL_SIZE_SLIM} />
                )}
                {typeof viewCount === 'number' && (
                    <Views title={longViews}>
                        <ViewsIcon color={colors.labelIcon} icon="view" height={20} width={20} />
                        {shortViews}
                    </Views>
                )}
            </Footer>
        </div>
    )
}

export default EditPanelInfoPreview
